import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import {
  FORGE_CHAIN_OPTIONS,
  SYSTEM_HEALTH_DEFAULT_FORGE_CHAIN_ID,
  type ForgeChainOption,
} from '../../config/chains';
import { numberParam, useUrlFilterState } from '../../hooks/useUrlFilterState';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  ForgeSystemHealthChainSnapshot,
  ForgeSystemHealthCheck,
  ForgeSystemHealthDashboardResponse,
  ForgeSystemHealthRefreshMode,
  ForgeSystemHealthRefreshRunResponse,
  ForgeSystemHealthSection,
  ForgeSystemHealthStatus,
} from '../../types';
import { buildMasterLabelUrl } from '../../utils/masterLabelLinks';
import {
  isForgeRefreshRunFresh,
  isForgeRefreshRunTimedOut,
  isTerminalForgeRefreshRunPollError,
  refreshRunCreatedAtMs,
} from '../../utils/refreshRuns';
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from '../../utils/safeStorage';

const DEFAULT_CHAIN_ID = SYSTEM_HEALTH_DEFAULT_FORGE_CHAIN_ID;
const CHAIN_OPTIONS = FORGE_CHAIN_OPTIONS;
const ACTIVE_SYSTEM_HEALTH_RUN_STORAGE_KEY = 'forge-system-health-active-run';
const NORMALIZED_BARCODE_TOO_SHORT_CHECK_KEY = 'design_normalized_barcode_too_short';
const NORMALIZED_BARCODE_TOO_LONG_CHECK_KEY = 'design_normalized_barcode_too_long';
const CROP_ITEM_NUMBER_ENCODED_BARCODE_CHECK_KEY = 'crop_item_number_encoded_normalized_barcode';
const DESIGN_ITEM_NUMBER_ENCODED_BARCODE_CHECK_KEY =
  'design_item_number_encoded_normalized_barcode';
const SUSPICIOUS_SCAN_BARCODE_CHECK_KEY = 'crop_suspicious_scan_normalized_barcode';
const PRODUCT_WITHOUT_ITEM_NUMBER_MAPPING_CHECK_KEY = 'product_without_item_number_mapping';
const DESIGN_SAMPLE_CHECK_KEYS = new Set([
  'invalid_design_document',
  'design_normalized_barcode_too_short',
  'design_normalized_barcode_too_long',
  'stale_effective_links',
  'missing_group_design',
  'wrong_chain_group_design',
  'representative_crop_mismatch',
  DESIGN_ITEM_NUMBER_ENCODED_BARCODE_CHECK_KEY,
]);
const CROP_SAMPLE_CHECK_KEYS = new Set([
  CROP_ITEM_NUMBER_ENCODED_BARCODE_CHECK_KEY,
  'crop_without_embedding',
  'pending_inactive_crop',
  'crop_missing_design',
  PRODUCT_WITHOUT_ITEM_NUMBER_MAPPING_CHECK_KEY,
]);

type NormalizedStatus = 'ok' | 'warning' | 'critical';
type SummaryMetricTone = 'neutral' | NormalizedStatus;
type ChainOption = ForgeChainOption;
type ActiveSystemHealthRun = {
  runId: string;
  chainId: number | null;
  createdAtMs: number;
  mode?: ForgeSystemHealthRefreshMode;
};
type NormalizedBarcodeLengthChecks = {
  shortCheck?: ForgeSystemHealthCheck;
  longCheck?: ForgeSystemHealthCheck;
  totalCount: number;
};
type ItemNumberEncodedBarcodeChecks = {
  cropCheck?: ForgeSystemHealthCheck;
  designCheck?: ForgeSystemHealthCheck;
  totalCount: number;
};

function isActiveSystemHealthRun(value: unknown): value is ActiveSystemHealthRun {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<ActiveSystemHealthRun>;
  return (
    typeof candidate.runId === 'string' &&
    (typeof candidate.chainId === 'number' || candidate.chainId === null) &&
    typeof candidate.createdAtMs === 'number' &&
    (!candidate.mode || candidate.mode === 'count_gate' || candidate.mode === 'full') &&
    isForgeRefreshRunFresh(candidate.createdAtMs)
  );
}

function readActiveSystemHealthRun(): ActiveSystemHealthRun | null {
  const raw = safeLocalStorageGetItem(ACTIVE_SYSTEM_HEALTH_RUN_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return isActiveSystemHealthRun(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeActiveSystemHealthRun(run: ActiveSystemHealthRun | null): void {
  safeLocalStorageSetItem(ACTIVE_SYSTEM_HEALTH_RUN_STORAGE_KEY, run ? JSON.stringify(run) : '');
}

function refreshRunDurationMs(
  run: Pick<ForgeSystemHealthRefreshRunResponse, 'createdAtUtc' | 'completedAtUtc'>
): number | null {
  if (!run.completedAtUtc) return null;
  const startedAt = Date.parse(run.createdAtUtc);
  const completedAt = Date.parse(run.completedAtUtc);
  if (!Number.isFinite(startedAt) || !Number.isFinite(completedAt)) return null;
  return Math.max(0, completedAt - startedAt);
}

function normalizeStatus(status: ForgeSystemHealthStatus): NormalizedStatus {
  if (status === 'critical') return 'critical';
  if (status === 'warning') return 'warning';
  return 'ok';
}

function statusLabel(status: ForgeSystemHealthStatus): string {
  const normalized = normalizeStatus(status);
  if (normalized === 'ok') return 'OK';
  return normalized;
}

function statusDotClass(status: ForgeSystemHealthStatus): string {
  const normalized = normalizeStatus(status);
  if (normalized === 'ok') return 'healthy';
  if (normalized === 'warning') return 'attention';
  return 'critical';
}

function statusIcon(status: ForgeSystemHealthStatus): React.ReactNode {
  const normalized = normalizeStatus(status);
  if (normalized === 'ok') return <CheckCircleOutlineIcon />;
  if (normalized === 'warning') return <WarningAmberOutlinedIcon />;
  return <ErrorOutlineIcon />;
}

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function formatTimestamp(value?: string | null): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatDuration(durationMs?: number | null): string {
  if (durationMs === undefined || durationMs === null) return '-';
  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  if (totalSeconds < 60) return `${totalSeconds}s`;
  return `${Math.floor(totalSeconds / 60)}m ${totalSeconds % 60}s`;
}

function ageSecondsFromTimestamp(value?: string | null): number | undefined {
  if (!value) return undefined;
  const timestampMs = new Date(value).getTime();
  if (!Number.isFinite(timestampMs)) return undefined;
  return Math.max(0, Math.floor((Date.now() - timestampMs) / 1000));
}

function formatRelativeAge(ageSeconds?: number): string {
  if (ageSeconds === undefined) return '-';
  if (ageSeconds < 60) return 'just now';
  if (ageSeconds < 3600) return `${Math.floor(ageSeconds / 60)}m ago`;
  if (ageSeconds < 86400) return `${Math.floor(ageSeconds / 3600)}h ago`;
  return `${Math.floor(ageSeconds / 86400)}d ago`;
}

function interpolateColor(
  start: [number, number, number],
  end: [number, number, number],
  ratio: number
): string {
  const clampedRatio = Math.max(0, Math.min(1, ratio));
  const [red, green, blue] = start.map((channel, index) =>
    Math.round(channel + (end[index] - channel) * clampedRatio)
  );
  return `rgb(${red}, ${green}, ${blue})`;
}

function snapshotAgeColor(ageSeconds: number): string {
  const oneHour = 60 * 60;
  const oneDay = 24 * oneHour;
  const twoDays = 2 * oneDay;

  if (ageSeconds <= oneHour) {
    return interpolateColor([65, 195, 91], [138, 183, 70], ageSeconds / oneHour);
  }
  if (ageSeconds <= oneDay) {
    return interpolateColor(
      [138, 183, 70],
      [238, 163, 55],
      (ageSeconds - oneHour) / (oneDay - oneHour)
    );
  }
  if (ageSeconds <= twoDays) {
    return interpolateColor([238, 163, 55], [232, 82, 63], (ageSeconds - oneDay) / oneDay);
  }
  return interpolateColor(
    [232, 82, 63],
    [255, 55, 85],
    Math.min(1, (ageSeconds - twoDays) / twoDays)
  );
}

const SYSTEM_HEALTH_URL_FILTERS = {
  chain: numberParam(DEFAULT_CHAIN_ID),
};

function sortedChains(chains: ForgeSystemHealthChainSnapshot[]): ForgeSystemHealthChainSnapshot[] {
  return [...chains].sort((left, right) => left.chain.chainId - right.chain.chainId);
}

function dashboardStatus(chains: ForgeSystemHealthChainSnapshot[]): NormalizedStatus {
  if (chains.some((chain) => normalizeStatus(chain.status) === 'critical')) return 'critical';
  if (chains.some((chain) => normalizeStatus(chain.status) === 'warning')) return 'warning';
  return 'ok';
}

function findDesignIntegrityCheck(
  snapshot: ForgeSystemHealthChainSnapshot,
  key: string
): ForgeSystemHealthCheck | undefined {
  return snapshot.designIntegrity.checks.find((check) => check.key === key);
}

function findEmbeddingSyncCheck(
  snapshot: ForgeSystemHealthChainSnapshot,
  key: string
): ForgeSystemHealthCheck | undefined {
  return snapshot.embeddingSync.checks.find((check) => check.key === key);
}

function checkMetricTone(check: ForgeSystemHealthCheck | undefined): SummaryMetricTone {
  if (!check) return 'neutral';
  if (check.count === 0) return 'ok';
  return normalizeStatus(check.status);
}

function aggregateBarcodeLengthTone(checks: NormalizedBarcodeLengthChecks): NormalizedStatus {
  if (checks.totalCount === 0) return 'ok';
  const tones = [checkMetricTone(checks.shortCheck), checkMetricTone(checks.longCheck)];
  if (tones.includes('critical')) return 'critical';
  if (tones.includes('warning')) return 'warning';
  return 'ok';
}

function aggregateItemNumberEncodedBarcodeTone(
  checks: ItemNumberEncodedBarcodeChecks
): NormalizedStatus {
  if (checks.totalCount === 0) return 'ok';
  const tones = [checkMetricTone(checks.cropCheck), checkMetricTone(checks.designCheck)];
  if (tones.includes('critical')) return 'critical';
  if (tones.includes('warning')) return 'warning';
  return 'ok';
}

function normalizedBarcodeLengthChecks(
  snapshot: ForgeSystemHealthChainSnapshot
): NormalizedBarcodeLengthChecks | null {
  const shortCheck = findDesignIntegrityCheck(snapshot, NORMALIZED_BARCODE_TOO_SHORT_CHECK_KEY);
  const longCheck = findDesignIntegrityCheck(snapshot, NORMALIZED_BARCODE_TOO_LONG_CHECK_KEY);
  if (!shortCheck && !longCheck) return null;
  return {
    shortCheck,
    longCheck,
    totalCount: (shortCheck?.count ?? 0) + (longCheck?.count ?? 0),
  };
}

function itemNumberEncodedBarcodeChecks(
  snapshot: ForgeSystemHealthChainSnapshot
): ItemNumberEncodedBarcodeChecks | null {
  const cropCheck = findEmbeddingSyncCheck(snapshot, CROP_ITEM_NUMBER_ENCODED_BARCODE_CHECK_KEY);
  const designCheck = findDesignIntegrityCheck(
    snapshot,
    DESIGN_ITEM_NUMBER_ENCODED_BARCODE_CHECK_KEY
  );
  if (!cropCheck && !designCheck) return null;
  return {
    cropCheck,
    designCheck,
    totalCount: (cropCheck?.count ?? 0) + (designCheck?.count ?? 0),
  };
}

function mergeDashboardResponse(
  previous: ForgeSystemHealthDashboardResponse | null,
  updated: ForgeSystemHealthDashboardResponse
): ForgeSystemHealthDashboardResponse {
  if (!previous || updated.chains.length !== 1) return updated;

  const updatedChain = updated.chains[0];
  const chains = previous.chains.some((chain) => chain.chain.chainId === updatedChain.chain.chainId)
    ? previous.chains.map((chain) =>
        chain.chain.chainId === updatedChain.chain.chainId ? updatedChain : chain
      )
    : [...previous.chains, updatedChain];
  const orderedChains = sortedChains(chains);
  return {
    ...previous,
    computedAtUtc: updated.computedAtUtc,
    status: dashboardStatus(orderedChains),
    issueCount: orderedChains.reduce((total, chain) => total + chain.issueCount, 0),
    supportedChainIds: [
      ...new Set([...previous.supportedChainIds, ...updated.supportedChainIds]),
    ].sort((left, right) => left - right),
    chains: orderedChains,
  };
}

function StatusBadge({ status }: { status: ForgeSystemHealthStatus }) {
  return (
    <span className={`system-health-status system-health-status--${normalizeStatus(status)}`}>
      <span className={`forge-health-dot ${statusDotClass(status)}`} />
      <span>{statusLabel(status)}</span>
    </span>
  );
}

function NotRefreshedBadge() {
  return (
    <span className="system-health-status system-health-status--muted">
      <span className="forge-health-dot neutral" />
      <span>Not refreshed</span>
    </span>
  );
}

function RefreshingBadge() {
  return (
    <span className="system-health-status system-health-status--refreshing">
      <span className="system-health-spinner system-health-spinner--small" />
      <span>Refreshing</span>
    </span>
  );
}

function SummaryMetric({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string | number;
  tone?: SummaryMetricTone;
}) {
  return (
    <div className={`system-health-metric system-health-metric--${tone}`}>
      <div className="system-health-metric-value">
        {typeof value === 'number' ? formatNumber(value) : value}
      </div>
      <div className="system-health-metric-label">{label}</div>
    </div>
  );
}

function sampleTargetId(sampleId: string): string {
  const parts = sampleId
    .split(':')
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length ? parts[parts.length - 1] : sampleId;
}

function systemHealthSampleUrl(
  check: ForgeSystemHealthCheck,
  chainId: number,
  sampleId: string
): string | null {
  const targetId = sampleTargetId(sampleId);
  if (DESIGN_SAMPLE_CHECK_KEYS.has(check.key)) {
    return buildMasterLabelUrl({ chainId, designId: targetId });
  }
  if (CROP_SAMPLE_CHECK_KEYS.has(check.key)) {
    return buildMasterLabelUrl({ chainId, cropIds: [targetId] });
  }
  return null;
}

function CheckRow({ check, chainId }: { check: ForgeSystemHealthCheck; chainId: number }) {
  return (
    <tr>
      <td>
        <div className="system-health-check-name">
          <span
            className={`system-health-check-icon system-health-check-icon--${normalizeStatus(check.status)}`}
          >
            {statusIcon(check.status)}
          </span>
          <span>{check.label}</span>
        </div>
        {check.detail ? <div className="system-health-check-detail">{check.detail}</div> : null}
      </td>
      <td className="system-health-number-cell">{formatNumber(check.count)}</td>
      <td>
        <StatusBadge status={check.status} />
      </td>
      <td>
        {check.sampleIds.length ? (
          <div className="system-health-samples">
            {check.sampleIds.map((sampleId) => {
              const sampleUrl = systemHealthSampleUrl(check, chainId, sampleId);
              return sampleUrl ? (
                <a
                  key={sampleId}
                  className="system-health-sample-link"
                  href={sampleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <code>{sampleId}</code>
                </a>
              ) : (
                <code key={sampleId}>{sampleId}</code>
              );
            })}
          </div>
        ) : (
          <span className="system-health-muted">-</span>
        )}
      </td>
    </tr>
  );
}

function SectionPanel({
  section,
  chainId,
}: {
  section: ForgeSystemHealthSection;
  chainId: number;
}) {
  return (
    <section className="system-health-section-panel">
      <div className="system-health-section-header">
        <div>
          <div className="system-health-section-title">{section.label}</div>
          <div className="system-health-section-subtitle">
            {formatNumber(section.issueCount)} open issue{section.issueCount === 1 ? '' : 's'}
          </div>
        </div>
        <StatusBadge status={section.status} />
      </div>
      <div className="system-health-table-wrap">
        <table className="system-health-check-table">
          <thead>
            <tr>
              <th>Check</th>
              <th className="system-health-number-cell">Count</th>
              <th>Status</th>
              <th>Samples</th>
            </tr>
          </thead>
          <tbody>
            {section.checks.map((check) => (
              <CheckRow key={check.key} check={check} chainId={chainId} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function issueCountTone(
  snapshot: ForgeSystemHealthChainSnapshot | null
): NormalizedStatus | 'muted' {
  if (!snapshot) return 'muted';
  if (snapshot.issueCount === 0) return 'ok';
  return normalizeStatus(snapshot.status);
}

function IssueCountCell({ snapshot }: { snapshot: ForgeSystemHealthChainSnapshot | null }) {
  const tone = issueCountTone(snapshot);
  return (
    <span className={`system-health-issue-count system-health-issue-count--${tone}`}>
      {snapshot ? formatNumber(snapshot.issueCount) : '-'}
    </span>
  );
}

function LastRefreshedCell({
  snapshot,
  durationMs,
  runningDurationMs,
}: {
  snapshot: ForgeSystemHealthChainSnapshot | null;
  durationMs?: number | null;
  runningDurationMs?: number | null;
}) {
  if (!snapshot && runningDurationMs === undefined) {
    return <span className="system-health-muted">Not refreshed</span>;
  }
  const ageSeconds = ageSecondsFromTimestamp(snapshot?.computedAtUtc);
  return (
    <div className="system-health-last-refresh-cell">
      <div className="system-health-last-refresh-primary">
        {runningDurationMs !== undefined && runningDurationMs !== null ? (
          <span className="system-health-running-duration">
            Refreshing {formatDuration(runningDurationMs)}
          </span>
        ) : snapshot ? (
          <span
            className="system-health-age"
            style={ageSeconds !== undefined ? { color: snapshotAgeColor(ageSeconds) } : undefined}
          >
            {formatRelativeAge(ageSeconds)}
          </span>
        ) : (
          <span className="system-health-muted">Not refreshed</span>
        )}
        {snapshot && runningDurationMs === undefined ? (
          <span className="system-health-refresh-duration">{formatDuration(durationMs)}</span>
        ) : null}
      </div>
      {snapshot ? (
        <div className="system-health-chain-meta">{formatTimestamp(snapshot.computedAtUtc)}</div>
      ) : null}
    </div>
  );
}

export const SystemHealthDashboard: React.FC = () => {
  const { token } = useAuthContext();
  const { showErrorToast } = useNotificationContext();
  const [dashboard, setDashboard] = useState<ForgeSystemHealthDashboardResponse | null>(null);
  const [filters, updateFilters] = useUrlFilterState(SYSTEM_HEALTH_URL_FILTERS);
  const selectedChainId = filters.chain;
  const selectedChainIdRef = useRef(selectedChainId);
  selectedChainIdRef.current = selectedChainId;
  const [isLoading, setIsLoading] = useState(false);
  const [activeRun, setActiveRun] = useState<ActiveSystemHealthRun | null>(() =>
    readActiveSystemHealthRun()
  );
  const [startingRefreshKey, setStartingRefreshKey] = useState<string | null>(null);
  const [startingRefreshMode, setStartingRefreshMode] =
    useState<ForgeSystemHealthRefreshMode | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshDurationsMs, setRefreshDurationsMs] = useState<Record<number, number>>({});
  const [allRefreshDurationMs, setAllRefreshDurationMs] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const pollErrorRunIdsRef = useRef<Set<string>>(new Set());
  const activeRunKey = activeRun
    ? activeRun.chainId === null
      ? 'all'
      : String(activeRun.chainId)
    : null;
  const refreshingKey = activeRunKey ?? startingRefreshKey;
  const refreshingMode = activeRun ? (activeRun.mode ?? 'full') : startingRefreshMode;
  const isFullRefreshAll = refreshingKey === 'all' && refreshingMode !== 'count_gate';
  const isQuickCheckAll = refreshingKey === 'all' && refreshingMode === 'count_gate';
  const activeRunElapsedMs = activeRun ? Math.max(0, nowMs - activeRun.createdAtMs) : null;

  const chains = useMemo(() => sortedChains(dashboard?.chains ?? []), [dashboard]);
  const loadedChainById = useMemo(
    () => new Map(chains.map((chain) => [chain.chain.chainId, chain])),
    [chains]
  );
  const supportedChainIds = dashboard?.supportedChainIds.length
    ? dashboard.supportedChainIds
    : CHAIN_OPTIONS.map((chain) => chain.id);
  const chainOptions: ChainOption[] = supportedChainIds.map((chainId) => {
    const loadedChain = loadedChainById.get(chainId);
    const knownChain = CHAIN_OPTIONS.find((chain) => chain.id === chainId);
    return {
      id: chainId,
      label: loadedChain?.chain.chainName || knownChain?.label || `Chain ${chainId}`,
    };
  });
  const chainRows = chainOptions.map((chain) => ({
    chain,
    snapshot: loadedChainById.get(chain.id) ?? null,
  }));
  const selectedChainOption =
    chainOptions.find((chain) => chain.id === selectedChainId) ??
    CHAIN_OPTIONS.find((chain) => chain.id === selectedChainId) ??
    ({ id: selectedChainId, label: `Chain ${selectedChainId}` } as ChainOption);
  const selectedChainRefreshKey = String(selectedChainOption.id);
  const isSelectedChainRefreshing = refreshingKey === selectedChainRefreshKey;
  const isFullRefreshingSelectedChain =
    isSelectedChainRefreshing && refreshingMode !== 'count_gate';
  const isQuickCheckingSelectedChain = isSelectedChainRefreshing && refreshingMode === 'count_gate';
  const selectedChain = useMemo(() => {
    return loadedChainById.get(selectedChainId) ?? null;
  }, [loadedChainById, selectedChainId]);
  const selectedBarcodeLengthChecks = selectedChain
    ? normalizedBarcodeLengthChecks(selectedChain)
    : null;
  const selectedItemNumberEncodedBarcodeChecks = selectedChain
    ? itemNumberEncodedBarcodeChecks(selectedChain)
    : null;
  const selectedSuspiciousScanBarcodeCheck = selectedChain
    ? findEmbeddingSyncCheck(selectedChain, SUSPICIOUS_SCAN_BARCODE_CHECK_KEY)
    : undefined;
  const selectedProductMappingCheck = selectedChain
    ? findDesignIntegrityCheck(selectedChain, PRODUCT_WITHOUT_ITEM_NUMBER_MAPPING_CHECK_KEY)
    : undefined;
  const hasSelectedSummaryMetrics = Boolean(
    selectedProductMappingCheck ||
    selectedSuspiciousScanBarcodeCheck ||
    selectedBarcodeLengthChecks ||
    selectedItemNumberEncodedBarcodeChecks
  );
  const criticalChainCount = chains.filter(
    (chain) => normalizeStatus(chain.status) === 'critical'
  ).length;
  const warningChainCount = chains.filter(
    (chain) => normalizeStatus(chain.status) === 'warning'
  ).length;
  const loadCachedDashboard = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await ForgeApiService.fetchSystemHealth(token);
      setDashboard(response);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load Forge system health';
      setLoadError(message);
      showErrorToast(message);
    } finally {
      setIsLoading(false);
    }
  }, [showErrorToast, token]);

  useEffect(() => {
    void loadCachedDashboard();
  }, [loadCachedDashboard]);

  useEffect(() => {
    writeActiveSystemHealthRun(activeRun);
  }, [activeRun]);

  useEffect(() => {
    if (!activeRun) return undefined;
    setNowMs(Date.now());
    const interval = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [activeRun]);

  const applyCompletedRefreshRun = useCallback(
    (run: ForgeSystemHealthRefreshRunResponse) => {
      if (!run.response) return;

      const response = run.response;
      const durationMs = refreshRunDurationMs(run);
      if (run.chainId === null || run.chainId === undefined) {
        setDashboard(response);
        setAllRefreshDurationMs(durationMs);
        setRefreshDurationsMs({});
        const currentChainId = selectedChainIdRef.current;
        updateFilters({
          chain:
            currentChainId &&
            response.chains.some((chain) => chain.chain.chainId === currentChainId)
              ? currentChainId
              : (response.chains[0]?.chain.chainId ?? DEFAULT_CHAIN_ID),
        });
        return;
      }

      const chainId = run.chainId;
      setDashboard((previous) => mergeDashboardResponse(previous, response));
      if (durationMs !== null) {
        setRefreshDurationsMs((previous) => ({ ...previous, [chainId]: durationMs }));
      }
      updateFilters({ chain: chainId });
    },
    [updateFilters]
  );

  useEffect(() => {
    if (!token || !activeRun) return undefined;

    const abortController = new AbortController();
    let isCancelled = false;

    const pollRun = async () => {
      if (isForgeRefreshRunTimedOut(activeRun.createdAtMs)) {
        setActiveRun((current) => (current?.runId === activeRun.runId ? null : current));
        pollErrorRunIdsRef.current.delete(activeRun.runId);
        showErrorToast('System health refresh timed out. Start a new refresh to retry.');
        return;
      }

      try {
        const run = await ForgeApiService.fetchSystemHealthRefreshRun(token, activeRun.runId, {
          signal: abortController.signal,
        });
        if (isCancelled || run.status === 'running') return;
        setActiveRun((current) => (current?.runId === activeRun.runId ? null : current));
        pollErrorRunIdsRef.current.delete(activeRun.runId);
        if (run.status === 'completed') {
          applyCompletedRefreshRun(run);
          return;
        }
        showErrorToast(run.error || 'System health refresh failed');
      } catch (error) {
        if (isCancelled || abortController.signal.aborted) return;
        if (!pollErrorRunIdsRef.current.has(activeRun.runId)) {
          pollErrorRunIdsRef.current.add(activeRun.runId);
          showErrorToast(
            error instanceof Error ? error.message : 'Failed to check system health refresh'
          );
        }
        if (isTerminalForgeRefreshRunPollError(error)) {
          setActiveRun((current) => (current?.runId === activeRun.runId ? null : current));
          pollErrorRunIdsRef.current.delete(activeRun.runId);
        }
      }
    };

    void pollRun();
    const interval = window.setInterval(() => void pollRun(), 5000);
    return () => {
      isCancelled = true;
      abortController.abort();
      window.clearInterval(interval);
    };
  }, [activeRun, applyCompletedRefreshRun, showErrorToast, token]);

  const startRefreshRun = useCallback(
    async (chainId: number | null, mode: ForgeSystemHealthRefreshMode = 'full') => {
      if (!token) return;
      const nextRefreshKey = chainId === null ? 'all' : String(chainId);
      setStartingRefreshKey(nextRefreshKey);
      setStartingRefreshMode(mode);
      try {
        const run = await ForgeApiService.createSystemHealthRefreshRun(
          token,
          chainId === null ? { mode } : { chain_id: chainId, mode }
        );
        if (run.status === 'completed') {
          applyCompletedRefreshRun(run);
          return;
        }
        if (run.status === 'failed') {
          showErrorToast(run.error || 'System health refresh failed');
          return;
        }
        const createdAtMs = refreshRunCreatedAtMs(run.createdAtUtc);
        if (isForgeRefreshRunTimedOut(createdAtMs)) {
          showErrorToast('System health refresh timed out. Start a new refresh to retry.');
          return;
        }
        setActiveRun({
          runId: run.runId,
          chainId: run.chainId ?? chainId,
          createdAtMs,
          mode,
        });
      } catch (error) {
        showErrorToast(error instanceof Error ? error.message : 'Failed to refresh system health');
      } finally {
        setStartingRefreshKey((current) => (current === nextRefreshKey ? null : current));
        setStartingRefreshMode((current) => (current === mode ? null : current));
      }
    },
    [applyCompletedRefreshRun, showErrorToast, token]
  );

  const refreshAll = useCallback(async () => {
    if (!token) return;
    await startRefreshRun(null);
  }, [startRefreshRun, token]);

  const quickCheckAll = useCallback(async () => {
    if (!token) return;
    await startRefreshRun(null, 'count_gate');
  }, [startRefreshRun, token]);

  const refreshChain = useCallback(
    async (chainId: number) => {
      if (!token) return;
      await startRefreshRun(chainId);
    },
    [startRefreshRun, token]
  );

  const quickCheckChain = useCallback(
    async (chainId: number) => {
      if (!token) return;
      await startRefreshRun(chainId, 'count_gate');
    },
    [startRefreshRun, token]
  );

  return (
    <div className="page-content page-content--wide rdc-dashboard-page system-health-page">
      <section className="forge-card rdc-dashboard-hero system-health-hero">
        <div className="system-health-hero-grid">
          <div>
            <div className="rdc-dashboard-title">System Health</div>
            <div className="rdc-dashboard-copy">
              Cached chain health snapshots and refresh status for Forge operational dashboards.
            </div>
          </div>
        </div>
        <div className="system-health-summary-grid">
          <SummaryMetric
            label="Loaded chains"
            value={`${chains.length}/${supportedChainIds.length || chains.length}`}
          />
          <SummaryMetric
            label="Open issues"
            value={dashboard?.issueCount ?? 0}
            tone={dashboard?.issueCount ? 'critical' : 'ok'}
          />
          <SummaryMetric label="Critical chains" value={criticalChainCount} tone="critical" />
          <SummaryMetric label="Warning chains" value={warningChainCount} tone="warning" />
        </div>
      </section>

      {isLoading && !dashboard ? (
        <section className="forge-card rdc-dashboard-card system-health-card">
          <div className="system-health-empty">Loading cached system health...</div>
        </section>
      ) : null}

      {loadError && !dashboard ? (
        <section className="forge-card rdc-dashboard-card system-health-card">
          <div className="system-health-error">{loadError}</div>
        </section>
      ) : null}

      {dashboard ? (
        <>
          <section className="forge-card rdc-dashboard-card system-health-card">
            <div className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title">
              <div className="system-health-card-title-left">
                <MonitorHeartOutlinedIcon />
                Chain status
              </div>
              <div className="system-health-controls">
                <button
                  type="button"
                  className={`system-health-refresh-button system-health-refresh-button--neutral ${
                    isFullRefreshAll ? 'is-refreshing' : ''
                  }`}
                  onClick={refreshAll}
                  disabled={!token || refreshingKey !== null || isLoading}
                >
                  {isFullRefreshAll ? (
                    <span className="system-health-spinner" />
                  ) : (
                    <RefreshOutlinedIcon />
                  )}
                  <span>
                    {isFullRefreshAll
                      ? activeRunElapsedMs !== null
                        ? `Refreshing all ${formatDuration(activeRunElapsedMs)}`
                        : 'Refreshing...'
                      : 'Refresh all'}
                  </span>
                </button>
                <button
                  type="button"
                  className="system-health-refresh-button system-health-refresh-button--subtle"
                  onClick={quickCheckAll}
                  disabled={!token || refreshingKey !== null || isLoading}
                  aria-label="Run quick check for all chains"
                  title="Run quick check for all chains"
                >
                  {isQuickCheckAll ? (
                    <span className="system-health-spinner" />
                  ) : (
                    <ManageSearchOutlinedIcon />
                  )}
                  <span>
                    {isQuickCheckAll
                      ? activeRunElapsedMs !== null
                        ? `Quick checking all ${formatDuration(activeRunElapsedMs)}`
                        : 'Checking...'
                      : 'Quick check all'}
                  </span>
                </button>
                {refreshingKey === 'all' && activeRunElapsedMs !== null ? (
                  <div className="system-health-control-note">
                    Current all-chain run {formatDuration(activeRunElapsedMs)}
                  </div>
                ) : allRefreshDurationMs !== null ? (
                  <div className="system-health-control-note">
                    Last all-chain run {formatDuration(allRefreshDurationMs)}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="system-health-table-wrap">
              <table className="system-health-chain-table">
                <colgroup>
                  <col className="system-health-col-chain" />
                  <col className="system-health-col-status" />
                  <col className="system-health-col-issues" />
                  <col className="system-health-col-count" />
                  <col className="system-health-col-embeddings" />
                  <col className="system-health-col-count" />
                  <col className="system-health-col-groups" />
                  <col className="system-health-col-refreshed" />
                  <col className="system-health-col-action" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Chain</th>
                    <th>Status</th>
                    <th className="system-health-number-cell">Issues</th>
                    <th className="system-health-number-cell">Crops</th>
                    <th className="system-health-number-cell">Embeddings</th>
                    <th className="system-health-number-cell">Designs</th>
                    <th className="system-health-number-cell">Groups</th>
                    <th>Last refreshed</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {chainRows.map(({ chain, snapshot }) => {
                    const isRefreshingChain =
                      refreshingKey === 'all' || refreshingKey === String(chain.id);
                    const isFullRefreshingChain =
                      isRefreshingChain && refreshingMode !== 'count_gate';
                    const isQuickCheckingChain =
                      isRefreshingChain && refreshingMode === 'count_gate';
                    return (
                      <tr
                        key={chain.id}
                        className={`${selectedChainId === chain.id ? 'is-selected' : ''} ${
                          isRefreshingChain ? 'is-refreshing' : ''
                        }`}
                        onClick={() => updateFilters({ chain: chain.id })}
                      >
                        <td>
                          <div className="system-health-chain-name">
                            {snapshot?.chain.chainName ?? chain.label}
                          </div>
                          <div className="system-health-chain-meta">{chain.id}</div>
                        </td>
                        <td>
                          {isRefreshingChain ? (
                            <RefreshingBadge />
                          ) : snapshot ? (
                            <StatusBadge status={snapshot.status} />
                          ) : (
                            <NotRefreshedBadge />
                          )}
                        </td>
                        <td className="system-health-number-cell">
                          <IssueCountCell snapshot={snapshot} />
                        </td>
                        <td className="system-health-number-cell">
                          {snapshot ? formatNumber(snapshot.totals.cropCount) : '-'}
                        </td>
                        <td className="system-health-number-cell">
                          {snapshot ? formatNumber(snapshot.totals.embeddingCount) : '-'}
                        </td>
                        <td className="system-health-number-cell">
                          {snapshot ? formatNumber(snapshot.totals.designCount) : '-'}
                        </td>
                        <td className="system-health-number-cell">
                          {snapshot ? formatNumber(snapshot.totals.designGroupCount) : '-'}
                        </td>
                        <td>
                          <LastRefreshedCell
                            snapshot={snapshot}
                            durationMs={snapshot?.computeDurationMs ?? refreshDurationsMs[chain.id]}
                            runningDurationMs={isRefreshingChain ? activeRunElapsedMs : undefined}
                          />
                        </td>
                        <td className="system-health-action-cell">
                          <div className="system-health-action-stack">
                            <button
                              type="button"
                              className={`system-health-refresh-button system-health-refresh-button--table ${
                                isFullRefreshingChain ? 'is-refreshing' : ''
                              }`}
                              aria-label={`Refresh ${snapshot?.chain.chainName ?? chain.label}`}
                              title={`Refresh ${snapshot?.chain.chainName ?? chain.label}`}
                              disabled={refreshingKey !== null || isLoading}
                              onClick={(event) => {
                                event.stopPropagation();
                                void refreshChain(chain.id);
                              }}
                            >
                              {isFullRefreshingChain ? (
                                <span className="system-health-spinner system-health-spinner--small" />
                              ) : (
                                <RefreshOutlinedIcon />
                              )}
                              <span>
                                {isFullRefreshingChain && activeRunElapsedMs !== null
                                  ? formatDuration(activeRunElapsedMs)
                                  : 'Refresh'}
                              </span>
                            </button>
                            <button
                              type="button"
                              className={`system-health-refresh-button system-health-refresh-button--table system-health-refresh-button--subtle ${
                                isQuickCheckingChain ? 'is-refreshing' : ''
                              }`}
                              aria-label={`Run quick check ${snapshot?.chain.chainName ?? chain.label}`}
                              title={`Run quick check ${snapshot?.chain.chainName ?? chain.label}`}
                              disabled={refreshingKey !== null || isLoading}
                              onClick={(event) => {
                                event.stopPropagation();
                                void quickCheckChain(chain.id);
                              }}
                            >
                              {isQuickCheckingChain ? (
                                <span className="system-health-spinner system-health-spinner--small" />
                              ) : (
                                <ManageSearchOutlinedIcon />
                              )}
                              <span>
                                {isQuickCheckingChain && activeRunElapsedMs !== null
                                  ? formatDuration(activeRunElapsedMs)
                                  : 'Quick check'}
                              </span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {selectedChain ? (
            <section className="forge-card rdc-dashboard-card system-health-card">
              <div className="system-health-detail-header">
                <div>
                  <div className="forge-card-title rdc-dashboard-card-title">
                    <MonitorHeartOutlinedIcon />
                    {selectedChain.chain.chainName}
                  </div>
                  <div className="system-health-detail-meta">
                    Chain {selectedChain.chain.chainId} -{' '}
                    {formatTimestamp(selectedChain.computedAtUtc)}
                  </div>
                </div>
                <StatusBadge status={selectedChain.status} />
              </div>
              {hasSelectedSummaryMetrics ? (
                <div className="system-health-selected-summary-grid">
                  {selectedProductMappingCheck ? (
                    <SummaryMetric
                      label="No item-number mapping"
                      value={selectedProductMappingCheck.count}
                      tone={checkMetricTone(selectedProductMappingCheck)}
                    />
                  ) : null}
                  {selectedItemNumberEncodedBarcodeChecks ? (
                    <SummaryMetric
                      label="Item-number barcodes"
                      value={selectedItemNumberEncodedBarcodeChecks.totalCount}
                      tone={aggregateItemNumberEncodedBarcodeTone(
                        selectedItemNumberEncodedBarcodeChecks
                      )}
                    />
                  ) : null}
                  {selectedSuspiciousScanBarcodeCheck ? (
                    <SummaryMetric
                      label="Suspicious scan barcodes"
                      value={selectedSuspiciousScanBarcodeCheck.count}
                      tone={checkMetricTone(selectedSuspiciousScanBarcodeCheck)}
                    />
                  ) : null}
                  {selectedBarcodeLengthChecks ? (
                    <>
                      <SummaryMetric
                        label="Short normalized barcodes"
                        value={selectedBarcodeLengthChecks.shortCheck?.count ?? 0}
                        tone={checkMetricTone(selectedBarcodeLengthChecks.shortCheck)}
                      />
                      <SummaryMetric
                        label="Long normalized barcodes"
                        value={selectedBarcodeLengthChecks.longCheck?.count ?? 0}
                        tone={checkMetricTone(selectedBarcodeLengthChecks.longCheck)}
                      />
                      <SummaryMetric
                        label="Barcode length issues"
                        value={selectedBarcodeLengthChecks.totalCount}
                        tone={aggregateBarcodeLengthTone(selectedBarcodeLengthChecks)}
                      />
                    </>
                  ) : null}
                </div>
              ) : null}
              <div className="system-health-section-grid">
                <SectionPanel
                  section={selectedChain.embeddingSync}
                  chainId={selectedChain.chain.chainId}
                />
                <SectionPanel
                  section={selectedChain.designIntegrity}
                  chainId={selectedChain.chain.chainId}
                />
              </div>
            </section>
          ) : (
            <section className="forge-card rdc-dashboard-card system-health-card">
              <div className="system-health-detail-header">
                <div>
                  <div className="forge-card-title rdc-dashboard-card-title">
                    <MonitorHeartOutlinedIcon />
                    {selectedChainOption.label}
                  </div>
                  <div className="system-health-detail-meta">
                    Chain {selectedChainOption.id} has not been refreshed in this view.
                  </div>
                </div>
                <NotRefreshedBadge />
              </div>
              <div className="system-health-action-stack">
                <button
                  type="button"
                  className={`system-health-refresh-button ${
                    isFullRefreshingSelectedChain ? 'is-refreshing' : ''
                  }`}
                  onClick={() => void refreshChain(selectedChainOption.id)}
                  disabled={refreshingKey !== null || isLoading}
                >
                  {isFullRefreshingSelectedChain ? (
                    <span className="system-health-spinner" />
                  ) : (
                    <RefreshOutlinedIcon />
                  )}
                  <span>
                    {isFullRefreshingSelectedChain
                      ? activeRunElapsedMs !== null
                        ? formatDuration(activeRunElapsedMs)
                        : 'Refreshing...'
                      : 'Refresh chain'}
                  </span>
                </button>
                <button
                  type="button"
                  className={`system-health-refresh-button system-health-refresh-button--subtle ${
                    isQuickCheckingSelectedChain ? 'is-refreshing' : ''
                  }`}
                  onClick={() => void quickCheckChain(selectedChainOption.id)}
                  disabled={refreshingKey !== null || isLoading}
                  aria-label={`Run quick check ${selectedChainOption.label}`}
                  title={`Run quick check ${selectedChainOption.label}`}
                >
                  {isQuickCheckingSelectedChain ? (
                    <span className="system-health-spinner" />
                  ) : (
                    <ManageSearchOutlinedIcon />
                  )}
                  <span>
                    {isQuickCheckingSelectedChain && activeRunElapsedMs !== null
                      ? formatDuration(activeRunElapsedMs)
                      : 'Quick check'}
                  </span>
                </button>
              </div>
            </section>
          )}
        </>
      ) : null}
    </div>
  );
};
